import { useMemo, useRef } from 'react';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import countries from 'i18n-iso-countries';
import frLocale from 'i18n-iso-countries/langs/fr.json';

import { DrawerV2, ButtonV2, dsV2 } from '@bloomays-lib/ui.shared';
import { AddressCountryCodeEU, type ISociety } from '@bloomays-lib/types.shared';
import { UPSERT_BLOOMER_SOCIETY, UNLINK_SOCIETY_TO_MISSION } from '@bloomays-lib/adapter.api-bloomer';

import { cancel, notify } from '../../../../helpers/toastify';
import { errorLogger } from '../../../../helpers/error';
import {
  buildBloomerSocietyUpsertInput,
  validateSupplierSocietyFormat,
  deriveSupplierSocietyMissingFields,
  type SupplierSocietyMissingField,
} from '../../../../helpers/societyForm';
import { useBloomerSocietyForm } from '../../useBloomerSocietyForm';
import { FieldV2, InputV2, SelectV2, Row2, InfoV2, CheckRow, type SelectV2Option } from '../formFields';

countries.registerLocale(frLocale);

export type EditBloomerSocietyDrawerProps = {
  open: boolean;
  onClose: () => void;
  society?: ISociety;
  missionRecordId: string;
  /** Rôle System : seul autorisé à modifier l'identité (SIRET) et une société de portage en place. */
  isSystem?: boolean;
  /** En création-changement : société précédente à détacher explicitement après création (pas d'unlink admin). */
  previousSocietyIdToUnlink?: string;
};

/** Drapeau emoji depuis un code ISO-2 (les <option> natives ne peuvent pas afficher d'image). */
const codeToFlag = (iso2: string): string =>
  iso2.toUpperCase().replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

/**
 * Gestion de la société du Bloomer (fournisseur) en panneau latéral V2 (DrawerV2).
 *
 * Présentation V2 (primitives + Dropzone) branchée sur le hook headless `useBloomerSocietyForm`, donc
 * les règles métier (statut → SIRET / TVA / pays, IBAN/BIC, KBIS / URSSAF / RCP) sont mono-sourcées
 * avec la créa. Spécificités du tableau de bord :
 * - `persist: false` → aucun brouillon en localStorage (édition ponctuelle).
 * - `requireCompleteness: false` → enregistrement PARTIEL autorisé ; ce qui peut manquer est signalé
 *   visuellement (encart + chips), sans bloquer.
 * - le FORMAT des champs renseignés reste validé (SIRET selon le pays, TVA, IBAN/BIC) via les règles
 *   partagées `validateSupplierSocietyFormat` — seule une valeur mal formée bloque la sauvegarde.
 */
export const EditBloomerSocietyDrawer = ({
  open,
  onClose,
  society,
  missionRecordId,
  isSystem = false,
  previousSocietyIdToUnlink,
}: EditBloomerSocietyDrawerProps) => {
  const { t } = useTranslation(['missionDashboard', 'notify', 'mission']);

  const MISSING_LABEL: Record<SupplierSocietyMissingField, string> = {
    name: t('drawers.editBloomerSociety.missingFields.name', { ns: 'missionDashboard' }),
    status: t('drawers.editBloomerSociety.missingFields.status', { ns: 'missionDashboard' }),
    siret: t('drawers.editBloomerSociety.missingFields.siret', { ns: 'missionDashboard' }),
    country: t('drawers.editBloomerSociety.missingFields.country', { ns: 'missionDashboard' }),
    tva: t('drawers.editBloomerSociety.missingFields.tva', { ns: 'missionDashboard' }),
    iban: t('drawers.editBloomerSociety.missingFields.iban', { ns: 'missionDashboard' }),
    bic: t('drawers.editBloomerSociety.missingFields.bic', { ns: 'missionDashboard' }),
    kbis: t('drawers.editBloomerSociety.missingFields.kbis', { ns: 'missionDashboard' }),
    urssaf: t('drawers.editBloomerSociety.missingFields.urssaf', { ns: 'missionDashboard' }),
  };
  const succeededRef = useRef(false);

  const [upsertBloomerSociety] = useMutation<{ upsertBloomerSociety: ISociety }>(UPSERT_BLOOMER_SOCIETY);
  const [unlinkSociety] = useMutation(UNLINK_SOCIETY_TO_MISSION);

  const upsertData = async (s: ISociety) => {
    succeededRef.current = false;
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { hideProgressBar: false, autoClose: 10000 });
    try {
      const upload = await upsertBloomerSociety({
        variables: { input: buildBloomerSocietyUpsertInput(s, missionRecordId) },
      });
      // Changement de société : la création lie la nouvelle, mais en admin l'ancienne n'est pas
      // détachée → on la détache explicitement de la mission.
      if (previousSocietyIdToUnlink) {
        await unlinkSociety({
          variables: { societyId: previousSocietyIdToUnlink, missionId: missionRecordId, role: 'bloomerBilling' },
        });
      }
      cancel();
      notify('success', t('updateSuccess', { ns: 'notify' }), null, { hideProgressBar: true, autoClose: 2000 });
      succeededRef.current = true;
      return upload?.data?.upsertBloomerSociety;
    } catch (error: any) {
      cancel();
      notify('error', t('randomUpdateError', { ns: 'notify' }), error);
      errorLogger(error, { extraInfos: 'editBloomerSocietyDrawerV2' });
    }
  };

  const form = useBloomerSocietyForm({ society, onValidated: upsertData, persist: false, requireCompleteness: false });
  const s = form.formSociety;

  // Société de portage = record PARTAGÉ : l'édition en place est verrouillée (sauf System) pour ne pas
  // écraser les coordonnées communes à tous les portés. On AFFICHE les champs (lecture seule) plutôt que
  // de les masquer, pour la compréhension.
  const isPortageLocked = Boolean(society?.portage) && !isSystem;
  // Champs sensibles (SIRET, IBAN, BIC) : modifiables uniquement par le rôle System lors de l'ÉDITION
  // d'une société existante ; en création ils restent libres. Une info-bulle l'indique toujours.
  const identityDisabled = Boolean(society) && !isSystem;

  // Info-bulle « seul System peut modifier » à accoler aux libellés SIRET / IBAN / BIC.
  const systemOnlyTip = () => (
    <Tooltip title={t('drawers.editBloomerSociety.systemOnlyHint', { ns: 'missionDashboard' })} arrow>
      <InfoOutlinedIcon
        sx={{ fontSize: 13, color: dsV2.color.t4, ml: '4px', verticalAlign: 'middle', cursor: 'help' }}
      />
    </Tooltip>
  );

  const setField = (patch: Partial<ISociety>) => form.setFormSociety({ ...s, ...patch } as ISociety);

  const statusOptions: SelectV2Option[] = form.selectSocietyOption.map((o) => ({
    value: String(o.value),
    label: typeof o.label === 'string' ? o.label : String(o.value),
    disabled: o.disabled,
  }));

  const allowedCountryCode = (code: string): boolean => {
    if (form.isForeignSociety) return code !== 'FR' && !AddressCountryCodeEU.includes(code);
    if (form.isEuropeanSociety) return code !== 'FR' && AddressCountryCodeEU.includes(code);
    return code === 'FR';
  };
  const countryOptions: SelectV2Option[] = useMemo(() => {
    const names = countries.getNames('fr', { select: 'official' });
    return Object.keys(names)
      .filter(allowedCountryCode)
      .map((code) => ({ value: code, label: `${codeToFlag(code)} ${names[code]}` }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.isForeignSociety, form.isEuropeanSociety]);

  const registrationLabel =
    form.fieldMode === 'european'
      ? t('drawers.editBloomerSociety.registrationEuropean', { ns: 'missionDashboard' })
      : form.fieldMode === 'foreign'
      ? t('drawers.editBloomerSociety.registrationForeign', { ns: 'missionDashboard' })
      : t('drawers.editBloomerSociety.registrationFrench', { ns: 'missionDashboard' });
  const registrationPlaceholder =
    form.fieldMode === 'european'
      ? t('drawers.editBloomerSociety.registrationPlaceholderEuropean', { ns: 'missionDashboard' })
      : form.fieldMode === 'foreign'
      ? t('drawers.editBloomerSociety.registrationPlaceholderForeign', { ns: 'missionDashboard' })
      : t('drawers.editBloomerSociety.registrationPlaceholderFrench', { ns: 'missionDashboard' });

  const formatErrors = useMemo(
    () => validateSupplierSocietyFormat(s ?? {}, { fieldMode: form.fieldMode, supportIban: form.supportIban }),
    [s, form.fieldMode, form.supportIban],
  );
  const hasFormatErrors = Object.keys(formatErrors).length > 0;

  const missing = useMemo(
    () => deriveSupplierSocietyMissingFields(s ?? {}, { hasKbis: form.hasKbisDoc, hasUrssaf: form.hasUrssafDoc }),
    [s, form.hasKbisDoc, form.hasUrssafDoc],
  );

  const handleSave = async () => {
    if (hasFormatErrors) return;
    await form.validate({ preventDefault: () => undefined });
    if (succeededRef.current) onClose();
  };

  const ibanLabel = form.supportIban ? t('ibanLabel', { ns: 'mission' }) : t('ibanFallbackLabel', { ns: 'mission' });
  const bicLabel = form.supportIban ? t('bicLabel', { ns: 'mission' }) : t('bicFallbackLabel', { ns: 'mission' });

  return (
    <DrawerV2
      open={open}
      onClose={onClose}
      title={t('drawers.editBloomerSociety.title', { ns: 'missionDashboard' })}
      subtitle={society?.name}
      width={520}
      footer={
        <>
          <ButtonV2 variant="secondary" onClick={onClose}>
            {t('drawers.common.cancel', { ns: 'missionDashboard' })}
          </ButtonV2>
          <ButtonV2
            onClick={handleSave}
            disabled={hasFormatErrors || isPortageLocked}
            loading={form.uploading}
            startIcon={<SaveRoundedIcon sx={{ fontSize: 15 }} />}
          >
            {t('drawers.common.save', { ns: 'missionDashboard' })}
          </ButtonV2>
        </>
      }
    >
      {isPortageLocked ? (
        <InfoV2 tone="amber" icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}>
          {t('drawers.editBloomerSociety.portageLockedWarning', { ns: 'missionDashboard' })}
        </InfoV2>
      ) : (
        <InfoV2 tone="blue" icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}>
          {t('drawers.editBloomerSociety.partialInfo', { ns: 'missionDashboard' })}
        </InfoV2>
      )}

      {missing.length > 0 ? (
        <InfoV2 tone="amber">
          {t('drawers.editBloomerSociety.missingForCompliance', {
            ns: 'missionDashboard',
            fields: missing.map((m) => MISSING_LABEL[m]).join(' · '),
          })}
        </InfoV2>
      ) : null}

      <FieldV2 label={t('drawers.common.name', { ns: 'missionDashboard' })}>
        <InputV2
          value={s?.name ?? ''}
          onChange={(v) => setField({ name: v })}
          placeholder={t('drawers.common.businessName', { ns: 'missionDashboard' })}
          disabled={isPortageLocked}
          ariaLabel="bloomerv2-name"
        />
      </FieldV2>

      <CheckRow
        checked={!!s?.portage}
        disabled={isPortageLocked}
        onChange={(checked) =>
          form.isMicroEntrepreneur && checked
            ? form.setFormSociety({ ...s, status: undefined, portage: true } as ISociety)
            : setField({ portage: checked })
        }
      >
        {t('drawers.editBloomerSociety.portageSociety', { ns: 'missionDashboard' })}
      </CheckRow>

      <Row2>
        <FieldV2 label={t('drawers.common.legalStatus', { ns: 'missionDashboard' })}>
          <SelectV2
            value={s?.status ?? ''}
            onChange={(v) => setField({ status: v })}
            options={statusOptions}
            placeholder={t('drawers.common.choose', { ns: 'missionDashboard' })}
            disabled={isPortageLocked}
            ariaLabel="bloomerv2-status"
          />
        </FieldV2>
        <FieldV2
          label={
            <>
              {registrationLabel}
              {systemOnlyTip()}
            </>
          }
          error={formatErrors.siret}
        >
          <InputV2
            value={s?.siret ?? ''}
            onChange={(v) => setField({ siret: v })}
            placeholder={registrationPlaceholder}
            tone={formatErrors.siret ? 'expired' : undefined}
            disabled={identityDisabled}
            ariaLabel="bloomerv2-siret"
          />
        </FieldV2>
      </Row2>

      {form.isForeignSociety || form.isEuropeanSociety ? (
        <FieldV2 label={t('drawers.editBloomerSociety.country', { ns: 'missionDashboard' })}>
          <SelectV2
            value={s?.addressCountryCode ?? ''}
            onChange={(v) => setField({ addressCountryCode: v })}
            options={countryOptions}
            placeholder={t('drawers.common.choose', { ns: 'missionDashboard' })}
            disabled={isPortageLocked}
            ariaLabel="bloomerv2-country"
          />
        </FieldV2>
      ) : null}

      {form.showVATTextField ? (
        <FieldV2
          label={t('drawers.common.tva', { ns: 'missionDashboard' })}
          error={formatErrors.tva}
          hint={
            form.isMicroEntrepreneur ? t('drawers.editBloomerSociety.tvaHint', { ns: 'missionDashboard' }) : undefined
          }
        >
          <InputV2
            value={s?.tva ?? ''}
            onChange={(v) => setField({ tva: v })}
            placeholder={t('drawers.editBloomerSociety.tvaPlaceholder', { ns: 'missionDashboard' })}
            tone={formatErrors.tva ? 'expired' : undefined}
            disabled={isPortageLocked}
            ariaLabel="bloomerv2-tva"
          />
        </FieldV2>
      ) : null}

      {/* Coordonnées bancaires toujours affichées (lecture seule hors System) pour la compréhension —
          y compris pour une société de portage : seul le rôle System peut les corriger. */}
      <Row2>
        <FieldV2
          label={
            <>
              {ibanLabel}
              {systemOnlyTip()}
            </>
          }
          error={formatErrors.iban}
        >
          <InputV2
            value={s?.IBAN ?? ''}
            onChange={(v) => setField({ IBAN: v })}
            placeholder={form.supportIban ? 'FR12 1234 1234 1234 1234 1234 12' : 'XXXXXXXX'}
            tone={formatErrors.iban ? 'expired' : undefined}
            disabled={identityDisabled}
            ariaLabel="bloomerv2-iban"
          />
        </FieldV2>
        <FieldV2
          label={
            <>
              {bicLabel}
              {systemOnlyTip()}
            </>
          }
          error={formatErrors.bic}
        >
          <InputV2
            value={s?.BIC ?? ''}
            onChange={(v) => setField({ BIC: v })}
            placeholder={t('drawers.editBloomerSociety.bicPlaceholder', { ns: 'missionDashboard' })}
            tone={formatErrors.bic ? 'expired' : undefined}
            disabled={identityDisabled}
            ariaLabel="bloomerv2-bic"
          />
        </FieldV2>
      </Row2>
    </DrawerV2>
  );
};

export default EditBloomerSocietyDrawer;
